// @file Renderers for Pragmatic DnD.
import window from '@@/bits/global'
import type { CleanupFn } from '@atlaskit/pragmatic-drag-and-drop/types'
import { PiniaVuePlugin } from 'pinia'
import Vue, { type Component, type VNodeData } from 'vue'

type ComponentProps = NonNullable<VNodeData['props']>

/**
 * Renders a native drag preview element.
 * @param container Container element given by Pragmatic DnD to put the preview in.
 * @param preview Vue component or HTML element to use as the drag preview.
 * @param props Props to be passed to the drag preview component.
 * @returns Function to clean up things before the container element is removed.
 */
const renderNativeDragPreview = <TProps extends ComponentProps>(
  container: HTMLElement,
  preview: Component | HTMLElement,
  props?: TProps,
): CleanupFn => {
  if (preview instanceof HTMLElement) {
    const el = preview.cloneNode(true) as HTMLElement
    container.appendChild(el)
    return () => {}
  }

  const el = document.createElement('div')
  container.appendChild(el)
  Vue.use(PiniaVuePlugin)
  // Since we are rendering a new vue app, the new vue app could be using pinia
  // as such, we need to initialize pinia for this new vue app.
  // We reuse the pinia instance initialized by the main app.
  const pinia = window?.app?.$pinia
  const app = new Vue({
    el,
    render: (h) => h(preview, { props }),
    pinia,
  })
  return () => app.$destroy()
}

export { renderNativeDragPreview }
export type { ComponentProps }
